/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { createLocalZustand } from "@/shared/state";
import { LinkItems } from "./side-menu";
import type { AppPopupProps } from "@/features/popups";

interface AppLayoutContextType {
  linkItems: LinkItems | any;
  appPopupProps: AppPopupProps;
  navigationBar: Array<JSX.Element | null>;
  hideSidebar: boolean;
  set: HandleChangeStore;
  get: () => AppLayoutContextType;
  updateNavigationBar: (component: JSX.Element | null, index?: number) => void;
  clearNavigationBar: () => void;
  updatePopupProps: HandleChangeValues;
  setHideSidebar: (hidden: boolean) => void;
}

export type HandleChangeStore = (
  partial:
    | AppLayoutContextType
    | Partial<AppLayoutContextType>
    | ((
        state: AppLayoutContextType
      ) => AppLayoutContextType | Partial<AppLayoutContextType>),
  replace?: boolean | undefined
) => void;

export type HandleChangeValues = (popupProps: Partial<AppPopupProps>) => void;

export const appLayoutContext = createLocalZustand<AppLayoutContextType>(
  (initializer) => (set, get) => ({
    linkItems: { ...initializer },
    navigationBar: [],
    hideSidebar: false,
    appPopupProps: { visible: false, type: "alert", onRight: () => {} },
    set: set,
    get: get,
/**
 * updateNavigationBar function.
 *
 * @param {*} component - Parameter description
 * @param {*} index - Parameter description
 *
 * @returns {*} Return value description
 */
    updateNavigationBar: (component, index) => {
      set((state) => {
        const updatedNavigationBar = [...state.navigationBar];

        // Early return if index is invalid
        if (index === undefined || index < 0) {
          return { ...state, navigationBar: updatedNavigationBar };
        }

        // Fill placeholders if needed
        if (index >= updatedNavigationBar.length) {
          const placeholders = Array(index - updatedNavigationBar.length).fill(
            null
          );
          updatedNavigationBar.push(...placeholders);
        }

        // Update navigation bar at index
        updatedNavigationBar.length = index + 1;
        updatedNavigationBar[index] = component;

        return { ...state, navigationBar: updatedNavigationBar };
      });
    },
/**
 * clearNavigationBar function.
 *
 * @returns {*} Return value description
 */
    clearNavigationBar: () => {
      set((state) => {
        return { ...state, navigationBar: [] };
      });
    },
/**
 * updatePopupProps function.
 *
 * @param {*} newValues - Parameter description
 *
 * @returns {*} Return value description
 */
    updatePopupProps: (newValues) =>
      set((state) => {
        const { appPopupProps } = get();
        const updatedAppPopupProps = { ...appPopupProps, ...newValues };
        return { ...state, appPopupProps: updatedAppPopupProps };
      }),
    setHideSidebar: (hidden: boolean) =>
      set((state) => ({ ...state, hideSidebar: hidden })),
  })
);

export const { useStore: useLayout } = appLayoutContext;
